// MaterialPreview — Live R3F preview for material graph edits.
// Renders the compiled graph material on a switchable mesh (sphere / plane /
// the current character) and live-updates as the graph changes. Shows shader
// compile errors loudly (red text). Mouse-driven orbit (no auto-rotate).

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { OrbitControls, Environment } from '@react-three/drei'
import * as THREE from 'three'
import { buildMaterialFromAsset } from '../../lib/avatarMaterial'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { setThumbnailCapturer } from '../../lib/materialThumbnail'

const MODES = [
  { key: 'sphere', label: 'Sphere' },
  { key: 'plane', label: 'Plane' },
  { key: 'character', label: 'Character' },
]

// Clone the loaded avatar's mesh geometry into a fresh, self-contained group
// (rest pose, plain meshes — no skeleton needed for a static material preview).
//
// These are SkinnedMeshes: their `matrixWorld` collapses the geometry (the
// visible shape comes from the skeleton), so baking it is wrong. The
// geometry-LOCAL vertex positions already hold the rest pose, and every
// MetaHuman part is authored in the same model space — so we clone each
// geometry and render it at identity. The combined geometry-local box is then
// recentered to the origin and scaled to ~1.8 units for the fixed camera.
// Returns { object, geos } or null (no avatar loaded).
function buildCharacterPreview() {
  const src = (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || null
  if (!src) return null

  const inner = new THREE.Group()
  const geos = []
  const box = new THREE.Box3()
  const tmp = new THREE.Box3()
  src.traverse((o) => {
    if (o.isMesh && o.geometry) {
      const g = o.geometry.clone()
      geos.push(g)
      inner.add(new THREE.Mesh(g)) // identity transform — geometry IS rest pose
      g.computeBoundingBox()
      box.union(tmp.copy(g.boundingBox))
    }
  })
  if (inner.children.length === 0 || box.isEmpty()) return null

  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const maxDim = Math.max(size.x, size.y, size.z) || 1

  inner.position.sub(center)          // recenter content at the origin
  const object = new THREE.Group()
  object.add(inner)
  object.scale.setScalar(1.8 / maxDim) // fit the fixed camera
  return { object, geos }
}

// Inner component that renders the chosen mesh + material. Must be wrapped in
// <Canvas> for useThree() to work.
function PreviewContent({ mode, graph, textureById, onError, onCharStatus }) {
  const materialRef = useRef(null)
  const [material, setMaterial] = useState(null)

  const sphereGeo = useMemo(() => new THREE.SphereGeometry(1, 64, 64), [])
  const planeGeo = useMemo(() => new THREE.PlaneGeometry(1.8, 1.8, 1, 1), [])
  useEffect(() => () => { sphereGeo.dispose(); planeGeo.dispose() }, [sphereGeo, planeGeo])

  // Build / tear down the character clone only while in character mode.
  const [charObj, setCharObj] = useState(null)
  useEffect(() => {
    if (mode !== 'character') { setCharObj(null); onCharStatus?.(true); return }
    const built = buildCharacterPreview()
    setCharObj(built?.object ?? null)
    onCharStatus?.(!!built)
    return () => { built?.geos.forEach(g => g.dispose()) }
  }, [mode, onCharStatus])

  // Rebuild material when the graph changes (debounced, with unmount guard).
  useEffect(() => {
    let alive = true
    const timer = setTimeout(() => {
      if (!alive) return
      try {
        const newMat = buildMaterialFromAsset({ type: 'graph', data: { graph } }, textureById)
        if (!alive) { newMat.dispose(); return }
        if (materialRef.current && materialRef.current !== newMat) materialRef.current.dispose()
        materialRef.current = newMat
        setMaterial(newMat)
        onError(null)
      } catch (e) {
        if (alive) { onError(e.message); setMaterial(null) }
      }
    }, 250)
    return () => {
      alive = false
      clearTimeout(timer)
      if (materialRef.current) { materialRef.current.dispose(); materialRef.current = null }
    }
  }, [graph, textureById, onError])

  // Bind the (rebuilt) material onto every character mesh.
  useEffect(() => {
    if (mode === 'character' && charObj && material) {
      charObj.traverse(o => { if (o.isMesh) o.material = material })
    }
  }, [mode, charObj, material])

  // Drive the graph material's shared uTime so time-based nodes (Panner, Time,
  // Noise distortion) animate IN THE PREVIEW. The avatar ticks uTime via
  // AvatarModel.useFrame, but this preview builds its own material that isn't in
  // that list, so without this the Panner looks frozen here. Reads the live ref
  // so a rebuilt material keeps animating.
  useFrame((state) => {
    const u = materialRef.current?.userData?.graphUniforms
    if (u?.uTime) u.uTime.value = state.clock.elapsedTime
  })

  // Register a thumbnail capturer so the graph editor can snapshot THIS live
  // render at commit time (see materialThumbnail.js). Renders one fresh frame
  // (preserveDrawingBuffer keeps it readable) then center-crops + downscales to
  // a 128² JPEG so the data URL stored on the material stays small (~5–15 KB).
  const { gl, scene, camera } = useThree()
  useEffect(() => setThumbnailCapturer(() => {
    gl.render(scene, camera)
    const src = gl.domElement
    if (!src.width || !src.height) return null
    const out = document.createElement('canvas')
    out.width = out.height = 128
    const ctx = out.getContext('2d')
    if (!ctx) return null
    const s = Math.min(src.width, src.height) // square cover-crop
    ctx.drawImage(src, (src.width - s) / 2, (src.height - s) / 2, s, s, 0, 0, 128, 128)
    return out.toDataURL('image/jpeg', 0.82)
  }), [gl, scene, camera])

  if (mode === 'character') {
    return charObj ? <primitive object={charObj} /> : null
  }

  const geometry = mode === 'plane' ? planeGeo : sphereGeo
  return (
    <mesh geometry={geometry}>
      {material ? <primitive object={material} /> : <meshStandardMaterial color="#cccccc" />}
    </mesh>
  )
}

// Resets the camera + orbit target to a sensible framing whenever the preview
// mode changes (otherwise the orbit zoom carries over between meshes and the
// character ends up cropped). Mounted after OrbitControls so `controls` exists.
function CameraRig({ mode }) {
  const { camera, controls } = useThree()
  useEffect(() => {
    camera.position.set(0, 0, mode === 'character' ? 3.2 : 2.5)
    camera.updateProjectionMatrix()
    if (controls?.target) { controls.target.set(0, 0, 0); controls.update() }
  }, [mode, camera, controls])
  return null
}

// Lighting setup for the preview — tuned so shading is visible
function PreviewLighting() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
      <pointLight position={[-3, 2, 2]} intensity={0.3} color="#8b6cff" />
      <Environment preset="studio" environmentIntensity={0.4} />
    </>
  )
}

export default function MaterialPreview({ graph, assetName = 'material', embedded = false }) {
  const [error, setError] = useState(null)
  const [mode, setMode] = useState('sphere')
  const [charOk, setCharOk] = useState(true)

  // Memoize textureById map to avoid per-render allocation
  const textureById = useMemo(
    () => new Map(
      CustomAssetManager.listTextureAssets().map(t => [t.id, t])
    ),
    [] // NOTE: relies on CustomAssetManager notifying externally if textures change
  )

  const isFloating = !embedded

  return (
    <div
      style={{
        position: isFloating ? 'absolute' : 'relative',
        bottom: isFloating ? 12 : undefined,
        right: isFloating ? 12 : undefined,
        width: isFloating ? 280 : '100%',
        height: isFloating ? 280 : '100%',
        background: isFloating ? 'var(--canvas)' : undefined,
        border: isFloating ? '1px solid var(--line)' : undefined,
        borderRadius: isFloating ? 6 : undefined,
        display: 'flex',
        flexDirection: 'column',
        zIndex: isFloating ? 100 : undefined,
        boxShadow: isFloating ? '0 4px 16px rgba(0,0,0,0.6)' : undefined,
        overflow: 'hidden',
      }}
    >
      {/* Header bar (only for floating) */}
      {isFloating && (
        <div
          style={{
            padding: '6px 8px',
            fontSize: 11,
            color: 'var(--fg-3)',
            borderBottom: '1px solid var(--line)',
            fontFamily: 'var(--m-font)',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          graphs/{assetName} preview
        </div>
      )}

      {/* Mesh switcher */}
      <div
        style={{
          display: 'flex', gap: 2, padding: 4,
          borderBottom: '1px solid var(--line)',
        }}
      >
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              flex: 1,
              fontFamily: 'var(--m-font)', fontSize: 10,
              padding: '4px 6px', borderRadius: 3, cursor: 'pointer',
              border: '1px solid ' + (mode === m.key ? 'var(--accent)' : 'var(--line-strong)'),
              background: mode === m.key ? 'color-mix(in srgb, var(--accent) 22%, transparent)' : 'var(--slot-bg)',
              color: mode === m.key ? 'var(--fg)' : 'var(--fg-2)',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Canvas container or error state */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: 'var(--slot-bg)' }}>
        {error ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              color: '#ff6b6b',
              fontSize: 10,
              fontFamily: 'var(--m-font)',
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
            }}
          >
            {error}
          </div>
        ) : (
          <>
            <Canvas
              style={{ width: '100%', height: '100%' }}
              camera={{ position: [0, 0, 2.5], fov: 50 }}
              dpr={1}
              gl={{ preserveDrawingBuffer: true }}
            >
              <PreviewLighting />
              <PreviewContent
                mode={mode}
                graph={graph}
                textureById={textureById}
                onError={setError}
                onCharStatus={setCharOk}
              />
              {/* Mouse-driven orbit; no auto-rotate. Pan + zoom enabled. */}
              <OrbitControls makeDefault enablePan enableZoom />
              <CameraRig mode={mode} />
            </Canvas>
            {mode === 'character' && !charOk && (
              <div
                style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 8, textAlign: 'center',
                  color: 'var(--fg-3)', fontSize: 10, fontFamily: 'var(--m-font)',
                  pointerEvents: 'none',
                }}
              >
                Character mesh not loaded.
                Open a character in the viewport first.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
